import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Calculator, Plus } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/shared/components/EmptyState';
import { useCreateEstimate, useEstimateList } from '../hooks/useEstimates';
import { errorMessage } from '../lib/errors';
import { EstimatesTable } from './EstimatesTable';

/** The opportunity page's Estimates tab: every estimate and revision of the opportunity, and New estimate. */
export function OpportunityEstimatesTab({ opportunityId }: { opportunityId: string }) {
  const { t } = useTranslation('crm');
  const navigate = useNavigate();
  const { data, isLoading, isError } = useEstimateList({ opportunityId, pageLength: 100 });
  const create = useCreateEstimate();

  const onCreate = async () => {
    try {
      const estimate = await create.mutateAsync(opportunityId);
      navigate(`/crm/estimates/${estimate.id}`);
    } catch (e) {
      toast.error(t('estimates.toast.createFailed'), { description: errorMessage(e) });
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">{t('estimates.title')}</h3>
        <Button size="sm" onClick={() => void onCreate()} disabled={create.isPending}>
          <Plus className="h-4 w-4 mr-1" /> {create.isPending ? t('estimates.creating') : t('estimates.newEstimate')}
        </Button>
      </div>
      {isError ? (
        <Alert variant="destructive"><AlertDescription>{t('estimates.loadFailed')}</AlertDescription></Alert>
      ) : isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : !data?.data.length ? (
        <EmptyState Icon={Calculator} heading={t('estimates.empty.opportunity')} />
      ) : (
        <EstimatesTable rows={data.data} perBudget />
      )}
    </div>
  );
}
